/* ============================================================================
   Uncaffed behaviour, product page and homepage

   Nine custom elements. Nothing else needs JS: the FAQ rows are native
   <details>, and add-to-cart is handled entirely by the theme's own
   <product-form> element (assets/theme.js), which builds FormData from the form.

   PURCHASE OPTIONS ARE SPLIT BY TEMPLATE, and this is the important bit:

     product page  Seal Subscriptions' own app block, which Tim placed there
     homepage      <unc-plan-picker>, because Seal CANNOT render there

   Seal's subscription widget is a PRODUCT-SCOPED app block, and
   templates/index.json has no product context, so Shopify discards it at render
   however it is declared. Verified empirically: a live dump of section.blocks on
   the homepage returned the three `plan` and three `kit_item` blocks with the
   app block absent entirely.

   So the picker below is not legacy. It is the homepage's only working selector,
   and its Seal defences still matter anywhere both could appear.
   See changes/rollback-2026-07-31-seal/README.md.

   Every define is guarded with customElements.get() so a duplicate include is
   harmless, matching the theme's own pattern. Every element degrades to
   something usable if this file fails to load, which is noted per element.

     <unc-plan-picker>  purchase options where Seal cannot render
     <unc-sticky-cta>   mobile add-to-cart bar
     <unc-reveal>       scroll-triggered .is-visible, drives the charts
     <unc-calculator>   the cost calculator
     <unc-orbit>        the orbital ingredient panels
     <unc-faq-tabs>     FAQ category pills
     <unc-hscroll>      mobile card carousel dots
     <unc-quotes>       testimonial spotlight, avatar pill selector
     <unc-gallery>      buy box images, thumbnail picker
============================================================================ */
(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     uncAutoCycle(host, step, ms)

     Shared slow autoplay for the two carousels. Written once because both need
     the SAME four pause conditions, and getting three of four right is worse
     than none: a carousel that keeps advancing under your finger, or behind a
     collapsed section, or while you are tabbing through it, is actively hostile.

       1. prefers-reduced-motion  never starts at all
       2. pointer over the host   pause  (hover, and touch via pointerenter)
       3. keyboard focus inside   pause  (:focus-within equivalent)
       4. scrolled out of view    pause  (IntersectionObserver)

     Condition 4 matters more than it looks: without it a carousel at the bottom
     of a 9,700px page cycles all the way through while nobody is looking, so the
     first thing a visitor sees on arrival is slide 3 of 3.

     Also pauses on document visibilitychange, so it does not burn a timer in a
     background tab.

     Returns a stop() so the element's disconnectedCallback can clean up.
  -------------------------------------------------------------------------- */
  function uncAutoCycle(host, step, ms) {
    /* Reduced motion wins unless the section explicitly opts out, which is the
       same pattern as .unc-hp-orb--always-spin, .unc-hp-tick--always-run and
       .unc-hp-beams--always-drift. Auto-advancing content is the strongest
       motion cue of the four, so the default here stays "respect the setting"
       and the merchant has to turn it on deliberately. */
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      host.dataset.autoplayReduced !== 'run'
    ) {
      return function () {};
    }

    var timer = null;

    /* SEED FROM CURRENT STATE, don't wait to be told.

       `offscreen` used to start `true` and only clear when IntersectionObserver
       first fired. If the observer is slow, or the document is hidden at load so
       it reports nothing, that blocker stays set and autoplay never starts at
       all. Same for `hidden`, which only moved on a visibilitychange event that
       had already happened before this ran.

       Both are seeded from the live values here, then kept fresh by their
       listeners. */
    var inView = function () {
      var r = host.getBoundingClientRect();
      return r.bottom > 0 && r.top < (window.innerHeight || 0) &&
             r.right > 0 && r.left < (window.innerWidth || 0);
    };
    var blockers = {
      hover: false,
      focus: false,
      offscreen: !inView(),
      hidden: !!document.hidden,
    };

    function tick() {
      // Re-check every blocker on each tick rather than clearing and
      // restarting the interval from six different handlers.
      for (var k in blockers) {
        if (blockers[k]) return;
      }
      step();
    }

    function start() {
      if (timer === null) timer = setInterval(tick, ms);
    }

    function stop() {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    }

    var set = function (key, val) {
      blockers[key] = val;
    };

    /* HOVER PAUSES ON THE CONTROLS, NOT ON THE WHOLE BAND.

       This used to listen on `host`. That is right for a small carousel and
       wrong for a full-width section: the Science mechanism is 826px tall and
       spans the page, so a cursor resting anywhere in it, which is where a
       cursor usually is while you read, pinned autoplay off permanently. It
       looked like the cycling had never been built.

       A host can name a smaller hover region with [data-autoplay-hover]. The
       point of the pause is not to yank a card away from someone about to click
       it, so the controls are the only part that needs to be protected. Falls
       back to the host, which keeps every existing caller behaving as before. */
    /* data-autoplay-hover="none" ON THE HOST opts out of hover pausing
       altogether, for a section that should simply keep running until the
       reader picks something. Distinct from the descendant [data-autoplay-hover]
       above, which narrows the region rather than disabling it: querySelector
       only looks at descendants, so the host's own attribute cannot collide. */
    if (host.dataset.autoplayHover !== 'none') {
      var hoverScope = host.querySelector('[data-autoplay-hover]') || host;
      hoverScope.addEventListener('pointerenter', function () { set('hover', true); });
      hoverScope.addEventListener('pointerleave', function () { set('hover', false); });
    }
    host.addEventListener('focusin', function () { set('focus', true); });
    host.addEventListener('focusout', function () { set('focus', false); });

    var onVis = function () { set('hidden', document.hidden); };
    document.addEventListener('visibilitychange', onVis);

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) { set('offscreen', !e.isIntersecting); });
        },
        { threshold: 0.35 }
      );
      io.observe(host);
    } else {
      blockers.offscreen = false; // no observer: fall back to always-on
    }

    start();

    return function teardown() {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      if (io) io.disconnect();
    };
  }

  /* --------------------------------------------------------------------------
     <unc-plan-picker>
     Three radio cards drive one hidden [name="selling_plan"] input.

     A one-off purchase must send NO selling_plan key at all, not an empty
     string, because an empty value is rejected by /cart/add on some API
     versions. Disabled inputs are excluded from FormData, so disabling the
     hidden input is how "no plan" is expressed.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-plan-picker')) {
    customElements.define(
      'unc-plan-picker',
      class UncPlanPicker extends HTMLElement {
        connectedCallback() {
          this.cards = Array.from(this.querySelectorAll('[data-unc-plan-card]'));
          this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
          if (!this.inputs.length) return;

          // The sticky bar lives outside this element.
          var root = this.closest('[data-unc-hero]') || document;
          this.barPrice = root.querySelector('[data-unc-bar-price]');
          this.barSuffix = root.querySelector('[data-unc-bar-suffix]');
          this.barLabel = root.querySelector('[data-unc-bar-label]');

          this.onChange = this.onChange.bind(this);
          this.addEventListener('change', this.onChange);

          // The multi-buy card's kit counter. Its buttons and number field are
          // interactive content inside the label, so the browser does not
          // select the radio for them; we do it here, then reprice the card.
          this.onQtyClick = this.onQtyClick.bind(this);
          this.onQtyInput = this.onQtyInput.bind(this);
          this.onQtyKey = this.onQtyKey.bind(this);
          this.addEventListener('click', this.onQtyClick);
          this.addEventListener('input', this.onQtyInput);
          this.addEventListener('keydown', this.onQtyKey);
          this.cards.forEach(this.repriceMulti, this);

          // Seal Subscriptions' script injects its own purchase-options widget
          // into any product form it finds and rewrites [name="selling_plan"]
          // to match ITS selection, which silently overwrites ours. Hiding the
          // widget does not stop the script. Re-assert our value on submit, in
          // the capture phase so this runs before <product-form> builds its
          // FormData, and whatever Seal did in between is undone.
          this.form = this.closest('form');
          if (this.form) {
            this.onSubmit = this.apply.bind(this);
            this.form.addEventListener('submit', this.onSubmit, true);
          }

          this.syncFromUrl();
          this.apply();
        }

        disconnectedCallback() {
          this.removeEventListener('change', this.onChange);
          this.removeEventListener('click', this.onQtyClick);
          this.removeEventListener('input', this.onQtyInput);
          this.removeEventListener('keydown', this.onQtyKey);
          if (this.form) this.form.removeEventListener('submit', this.onSubmit, true);
        }

        onQtyClick(e) {
          var btn = e.target.closest('[data-unc-qty-dec], [data-unc-qty-inc]');
          if (!btn || !this.contains(btn)) return;
          e.preventDefault();
          var card = btn.closest('[data-unc-plan-card]');
          var field = card && card.querySelector('[data-unc-qty-input]');
          if (!field) return;
          var step = btn.hasAttribute('data-unc-qty-inc') ? 1 : -1;
          field.value = String((parseInt(field.value, 10) || 0) + step);
          this.commitQty(card);
        }

        onQtyInput(e) {
          if (!e.target.hasAttribute || !e.target.hasAttribute('data-unc-qty-input')) return;
          // Let the field be emptied mid-edit; clamp once it holds a number.
          if (e.target.value === '') return;
          this.commitQty(e.target.closest('[data-unc-plan-card]'));
        }

        // Enter in the number field would submit the form and add to cart.
        onQtyKey(e) {
          if (e.key !== 'Enter') return;
          if (!e.target.hasAttribute || !e.target.hasAttribute('data-unc-qty-input')) return;
          e.preventDefault();
          e.target.blur();
        }

        // Clamp the counter, reprice the card, select it, then push the
        // quantity into the form through apply().
        commitQty(card) {
          if (!card) return;
          var radio = card.querySelector('input[type="radio"]');
          var field = card.querySelector('[data-unc-qty-input]');
          if (!radio || !field) return;
          var min = parseInt(radio.dataset.qtyMin, 10) || 1;
          var max = parseInt(radio.dataset.qtyMax, 10) || 99;
          var n = parseInt(field.value, 10);
          if (isNaN(n)) n = min;
          n = Math.min(max, Math.max(min, n));
          field.value = String(n);
          radio.dataset.quantity = String(n);
          this.repriceMulti(card);
          radio.checked = true;
          this.apply();
        }

        // Tier maths for a multi-buy card: from tier_b_from kits the lower
        // per-kit price applies, else the tier-1 price. Display only; Shopify's
        // automatic discount is what actually charges it.
        repriceMulti(card) {
          var radio = card.querySelector('input[type="radio"][data-multi]');
          if (!radio) return;
          var qty = parseInt(radio.dataset.quantity, 10) || 1;
          var bFrom = parseInt(radio.dataset.tierBFrom, 10) || 4;
          var per = qty >= bFrom ? radio.dataset.tierBPrice : radio.dataset.tierAPrice;
          per = parseFloat(per) || 0;
          var sym = radio.dataset.symbol || '';
          // Whole pounds print without pence; anything else prints both
          // decimals, so 16.5 reads £16.50 rather than £16.5.
          var fmt = function (v) {
            var r = Math.round(v * 100) / 100;
            return sym + (r % 1 === 0 ? r.toString() : r.toFixed(2));
          };
          var amount = card.querySelector('[data-unc-price-amount]');
          var note = card.querySelector('[data-unc-price-note]');
          if (amount) amount.textContent = fmt(per);
          if (note) note.textContent = fmt(per * qty) + ' for ' + qty + ' kits';
          radio.dataset.price = fmt(per);

          // Saving against the live single-kit price, and the "one more kit"
          // nudge one below tier 2. Same rules as the Liquid first paint.
          var single = (parseInt(radio.dataset.singlePence, 10) || 0) / 100;
          var aPrice = parseFloat(radio.dataset.tierAPrice) || 0;
          var bPrice = parseFloat(radio.dataset.tierBPrice) || 0;
          var saving = single * qty - per * qty;
          var save = card.querySelector('[data-unc-save]');
          if (save) {
            var saveText = radio.dataset.savingText || '';
            if (saving > 0.001 && saveText) {
              save.textContent = saveText.split('{saving}').join(fmt(saving)).split('{qty}').join(String(qty));
              save.hidden = false;
            } else {
              save.textContent = '';
              save.hidden = true;
            }
          }
          var nudge = card.querySelector('[data-unc-nudge]');
          if (nudge) {
            var nudgeText = radio.dataset.nudgeText || '';
            var diff = bFrom * bPrice - (bFrom - 1) * aPrice;
            if (qty === bFrom - 1 && diff > 0 && nudgeText) {
              nudge.textContent = nudgeText.split('{diff}').join(fmt(diff)).split('{tier_b}').join(fmt(bPrice));
              nudge.hidden = false;
            } else {
              nudge.textContent = '';
              nudge.hidden = true;
            }
          }
          var dec = card.querySelector('[data-unc-qty-dec]');
          var inc = card.querySelector('[data-unc-qty-inc]');
          var min = parseInt(radio.dataset.qtyMin, 10) || 1;
          var max = parseInt(radio.dataset.qtyMax, 10) || 99;
          if (dec) dec.disabled = qty <= min;
          if (inc) inc.disabled = qty >= max;
        }

        // Set the kit count from outside, e.g. the kit quiz's result button
        // (assets/unc-kitquiz.js). 2 or more goes on the multi-buy card through
        // commitQty, so the clamp, repricing and form quantity are the same as
        // a tap on its counter; 1 selects the one-off card.
        setQty(n) {
          n = parseInt(n, 10) || 1;
          var multi = this.inputs.filter(function (i) {
            return i.hasAttribute('data-multi');
          })[0];
          var min = multi ? parseInt(multi.dataset.qtyMin, 10) || 2 : 2;
          if (multi && n >= min) {
            var card = multi.closest('[data-unc-plan-card]');
            var field = card && card.querySelector('[data-unc-qty-input]');
            if (field) {
              field.value = String(n);
              this.commitQty(card);
              return;
            }
          }
          var single = this.inputs.filter(function (i) {
            return !i.hasAttribute('data-multi') && !i.dataset.sellingPlan;
          })[0];
          if (single) {
            single.checked = true;
            this.apply();
          }
        }

        // Honour ?selling_plan=<id> so deep links land on the right card.
        syncFromUrl() {
          var wanted;
          try {
            wanted = new URLSearchParams(window.location.search).get('selling_plan');
          } catch (e) {
            return;
          }
          if (!wanted) return;
          var match = this.inputs.filter(function (i) {
            return i.dataset.sellingPlan === wanted;
          })[0];
          if (match) match.checked = true;
        }

        onChange() {
          this.apply();
        }

        // Never cache this node and never match on our own data attribute.
        // Seal Subscriptions REPLACES the selling_plan input with one of its
        // own (same name, our attribute gone), so a cached reference or an
        // attribute selector both end up pointing at nothing and the plan is
        // silently dropped, turning every subscription into a one-off. Re-query
        // by name each time, and recreate the input if Seal removed it outright.
        sellingPlanInput() {
          if (!this.form) return null;
          var el = this.form.querySelector('input[name="selling_plan"]');
          if (!el) {
            el = document.createElement('input');
            el.type = 'hidden';
            el.name = 'selling_plan';
            this.form.appendChild(el);
          }
          el.setAttribute('data-unc-selling-plan', '');
          return el;
        }

        apply() {
          var checked =
            this.inputs.filter(function (i) {
              return i.checked;
            })[0] || this.inputs[0];
          if (!checked) return;
          checked.checked = true;

          // .is-selected backs up :has(input:checked) for Firefox < 121.
          this.cards.forEach(function (card) {
            var input = card.querySelector('input[type="radio"]');
            card.classList.toggle('is-selected', !!input && input.checked);
          });

          var plan = checked.dataset.sellingPlan || '';
          var hidden = this.sellingPlanInput();
          if (hidden) {
            if (plan) {
              hidden.value = plan;
              hidden.disabled = false;
            } else {
              hidden.value = '';
              hidden.disabled = true; // excluded from FormData entirely
            }
          }

          // Multi-buy cards carry a kit count. Re-query by name each time for
          // the same reason as sellingPlanInput(): Seal rebuilds the form.
          var qty = this.form && this.form.querySelector('input[name="quantity"]');
          if (qty) qty.value = checked.dataset.quantity || '1';

          if (this.barPrice) this.barPrice.textContent = checked.dataset.price || '';
          if (this.barSuffix) this.barSuffix.textContent = checked.dataset.suffix || '';
          if (this.barLabel) this.barLabel.textContent = checked.dataset.label || '';

          // For listeners such as the kit quiz, which logs a changed count.
          this.dispatchEvent(
            new CustomEvent('unc:plan-change', { bubbles: true, detail: { qty: parseInt(checked.dataset.quantity, 10) || 1 } })
          );
        }
      }
    );
  }

  /* --------------------------------------------------------------------------
     <unc-sticky-cta>
     Reveals the mobile add-to-cart bar once the real buy box has scrolled out
     of view. IntersectionObserver on the button, no scroll listener.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-sticky-cta')) {
    customElements.define(
      'unc-sticky-cta',
      class UncStickyCta extends HTMLElement {
        connectedCallback() {
          var targetSel = this.dataset.watch;
          var target = targetSel ? document.querySelector(targetSel) : null;
          if (!target || !('IntersectionObserver' in window)) {
            this.classList.add('is-visible');
            return;
          }
          var self = this;
          this.observer = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                // show the bar only once the primary button is off screen
                self.classList.toggle('is-visible', !entry.isIntersecting);
              });
            },
            { rootMargin: '0px 0px -10% 0px' }
          );
          this.observer.observe(target);
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }
      }
    );
  }

  /* --------------------------------------------------------------------------
     <unc-reveal>
     Adds .is-visible once scrolled into view, then stops observing. Drives the
     protocol chart draw-on and the sleep-chart bars.

     TWO CLASSES, NOT ONE. `is-armed` goes on immediately at upgrade; only then
     may CSS hide anything. Content that starts at opacity 0 and waits for
     .is-visible is invisible forever if this element never upgrades, and
     "nothing animated" is a far better failure than "nothing is there". Hiding
     is therefore conditional on the machinery being present and running.

     There is also a deadline. IntersectionObserver reports nothing while the
     document is hidden, so a page opened in a background tab, restored from
     bfcache, or prerendered can sit armed with no callback ever arriving. If
     .is-visible has not landed within two seconds of the document becoming
     visible, it is set regardless.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-reveal')) {
    customElements.define(
      'unc-reveal',
      class UncReveal extends HTMLElement {
        connectedCallback() {
          var self = this;
          this.classList.add('is-armed');

          if (!('IntersectionObserver' in window)) {
            this.show();
            return;
          }

          this.observer = new IntersectionObserver(
            function (entries, obs) {
              entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                self.show();
                obs.unobserve(entry.target);
              });
            },
            { threshold: parseFloat(this.dataset.threshold || '0.2') }
          );
          this.observer.observe(this);

          /* THE DEADLINE MUST CHECK THE VIEWPORT BEFORE FIRING.

             This used to call show() unconditionally two seconds after the
             document became visible, which revealed EVERY <unc-reveal> on the
             page whether or not it had been scrolled to. The sleep charts on
             the Science page were playing their whole build-in while still
             thousands of pixels below the fold, so by the time you reached them
             they were already finished and sat there static. That is the exact
             fault the reveal exists to prevent.

             It is still a safety net, just a narrower one: it only rescues an
             element that IS in view and that the observer has somehow not
             reported, which is the bfcache and prerender case it was written
             for. Anything below the fold is left to the observer. */
          this.onVis = function () {
            if (document.hidden) return;
            clearTimeout(self.deadline);
            self.deadline = setTimeout(function () {
              if (self.classList.contains('is-visible')) return;
              var r = self.getBoundingClientRect();
              var inView = r.bottom > 0 && r.top < (window.innerHeight || 0);
              if (inView) self.show();
            }, 2000);
          };
          document.addEventListener('visibilitychange', this.onVis);
          this.onVis();
        }

        show() {
          clearTimeout(this.deadline);
          this.classList.add('is-visible');
          if (this.observer) this.observer.disconnect();
        }

        disconnectedCallback() {
          clearTimeout(this.deadline);
          if (this.onVis) document.removeEventListener('visibilitychange', this.onVis);
          if (this.observer) this.observer.disconnect();
        }
      }
    );
  }

  /* --------------------------------------------------------------------------
     <unc-calculator>
     Two sliders, three output tiles.

       mgNow     = cups * mgPerDrink
       spendYear = round(cups * price * 365)

     savedYear used to be a third output. Removed with the tile that showed it.

     Price is carried in pence as an integer because Shopify range settings
     mishandle decimal steps. Everything is server-rendered first, so the tiles
     have real text before this runs.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-calculator')) {
    customElements.define(
      'unc-calculator',
      class UncCalculator extends HTMLElement {
        connectedCallback() {
          this.cupsInput = this.querySelector('[data-unc-cups]');
          this.priceInput = this.querySelector('[data-unc-price]');
          if (!this.cupsInput || !this.priceInput) return;

          this.cupsOut = this.querySelector('[data-unc-cups-value]');
          this.priceOut = this.querySelector('[data-unc-price-value]');
          this.mgOut = this.querySelector('[data-unc-mg-now]');
          this.spendOut = this.querySelector('[data-unc-spend-year]');

          this.mgPerDrink = parseFloat(this.dataset.mgPerDrink || '95');
          this.unit = this.dataset.unit || 'mg';

          this.money = this.makeFormatter(0);
          this.money2 = this.makeFormatter(2);

          this.onInput = this.render.bind(this);
          this.cupsInput.addEventListener('input', this.onInput);
          this.priceInput.addEventListener('input', this.onInput);

          this.render();
        }

        disconnectedCallback() {
          if (!this.cupsInput) return;
          this.cupsInput.removeEventListener('input', this.onInput);
          this.priceInput.removeEventListener('input', this.onInput);
        }

        makeFormatter(digits) {
          var currency = this.dataset.currency || 'GBP';
          var locale = this.dataset.locale || 'en-GB';
          var symbol = this.dataset.symbol || '£';
          try {
            var fmt = new Intl.NumberFormat(locale, {
              style: 'currency',
              currency: currency,
              minimumFractionDigits: digits,
              maximumFractionDigits: digits
            });
            return function (n) {
              return fmt.format(n);
            };
          } catch (e) {
            return function (n) {
              return symbol + n.toFixed(digits);
            };
          }
        }

        render() {
          var cups = parseInt(this.cupsInput.value, 10) || 0;
          var price = (parseInt(this.priceInput.value, 10) || 0) / 100;

          var mgNow = Math.round(cups * this.mgPerDrink);
          var spendYear = Math.round(cups * price * 365);

          if (this.cupsOut) this.cupsOut.textContent = String(cups);
          if (this.priceOut) this.priceOut.textContent = this.money2(price);
          if (this.mgOut) this.mgOut.textContent = mgNow + this.unit;
          if (this.spendOut) this.spendOut.textContent = this.money(spendYear);
        }
      }
    );
  }

  /* --------------------------------------------------------------------------
     <unc-orbit>
     One orbital ingredient panel: a spinning ring of nodes around a static
     capsule, each node opening a detail card.

     All of the geometry, the spin and the counter-spin that keeps the nodes
     upright are pure CSS (see unc-hp.css). This element only tracks which node
     is open, which it does with one attribute on the host plus one class on the
     stage, so the CSS does all the showing and hiding.

     Pausing is deliberately driven by a single inherited custom property set on
     the stage, never by toggling animation-play-state on the ring and the
     counters separately: one write repaints them in the same style
     recalculation, so they cannot drift out of phase.

     Without JS the buttons are inert and the detail cards stay hidden, but the
     orbit still turns and the panel still reads correctly.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-orbit')) {
    customElements.define(
      'unc-orbit',
      class UncOrbit extends HTMLElement {
        connectedCallback() {
          this.stage = this.querySelector('[data-orb-stage]') || this;
          this.nodes = Array.from(this.querySelectorAll('[data-orb-node]'));
          if (!this.nodes.length) return;

          this.details = Array.from(this.querySelectorAll('[data-orb-detail]'));
          this.ring = this.querySelector('[data-orb-ring]');
          this.counters = Array.from(this.querySelectorAll('[data-orb-counter]'));
          this.hint = this.querySelector('[data-orb-hint]');
          this.panel = this.dataset.orbPanel || '';

          if (this.dataset.orbHoverPause === 'true') {
            this.stage.classList.add('is-hoverpause');
          }

          // One delegated listener for the whole panel.
          this.onClick = this.onClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.onBroadcast = this.onBroadcast.bind(this);
          this.addEventListener('click', this.onClick);
          this.addEventListener('keydown', this.onKeydown);
          // L-theanine appears in both panels, so activating it in one has to
          // highlight it in the other. Each panel decides for itself.
          document.addEventListener('unc-orbit:activate', this.onBroadcast);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.removeEventListener('keydown', this.onKeydown);
          document.removeEventListener('unc-orbit:activate', this.onBroadcast);
        }

        onClick(e) {
          if (e.target.closest('[data-orb-close]')) {
            this.clear();
            return;
          }
          var pair = e.target.closest('[data-orb-pair]');
          if (pair) {
            /* The pairing chip is the ONE case that is meant to reach across
               into the other panel, so it broadcasts with pair: true. */
            this.broadcast(pair.dataset.orbPair, true);
            return;
          }
          var node = e.target.closest('[data-orb-node]');
          if (node) {
            this.broadcast(node.dataset.orbKey);
            return;
          }
          // Clicks inside an open card must not close it.
          if (e.target.closest('[data-orb-detail]')) return;
          this.clear();
        }

        onKeydown(e) {
          if (e.key === 'Escape') {
            this.clear();
            return;
          }
          var node = e.target.closest('[data-orb-node]');
          if (!node) return;
          var i = this.nodes.indexOf(node);
          var next = -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % this.nodes.length;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            next = (i - 1 + this.nodes.length) % this.nodes.length;
          }
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = this.nodes.length - 1;
          if (next < 0) return;
          e.preventDefault();
          this.nodes[next].focus();
          this.broadcast(this.nodes[next].dataset.orbKey);
        }

        broadcast(key, pair) {
          if (!key) return;
          this.dispatchEvent(
            new CustomEvent('unc-orbit:activate', {
              bubbles: true,
              detail: { key: key, pair: pair === true },
            })
          );
        }

        onBroadcast(e) {
          var d = e.detail || {};
          var key = d.key;
          if (!key) return;

          /* L-theanine is in BOTH panels, so a plain node click used to open it
             in both at once: every panel heard the event, saw it had that
             ingredient, and opened its own card. Two detail cards for one click.

             Only the pairing chip is supposed to cross panels. A normal click
             opens the panel it happened in and closes the other one; the event
             bubbles from the originating <unc-orbit>, so e.target identifies it. */
          if (!d.pair && e.target !== this) {
            this.clear();
            return;
          }

          // Own it if this panel has that ingredient, otherwise stand down.
          var mine = this.nodes.filter(function (n) {
            return n.dataset.orbKey === key;
          })[0];
          if (mine) this.activate(key);
          else this.clear();
        }

        activate(key) {
          if (this.dataset.open === key) {
            this.clear();
            return;
          }
          this.dataset.open = key;
          this.park(key);
          this.stage.classList.add('is-paused');
          this.nodes.forEach(function (n) {
            var on = n.dataset.orbKey === key;
            n.setAttribute('aria-selected', on ? 'true' : 'false');
            n.tabIndex = on ? 0 : -1;
            if (n.parentNode) n.parentNode.classList.toggle('is-active', on);
          });
          this.details.forEach(function (d) {
            d.hidden = d.dataset.orbDetail !== key;
          });
          if (this.hint) this.hint.hidden = true;
        }

        clear() {
          delete this.dataset.open;
          this.unpark();
          this.stage.classList.remove('is-paused');
          this.nodes.forEach(function (n, i) {
            n.setAttribute('aria-selected', 'false');
            n.tabIndex = i === 0 ? 0 : -1;
            if (n.parentNode) n.parentNode.classList.remove('is-active');
          });
          this.details.forEach(function (d) {
            d.hidden = true;
          });
          if (this.hint) this.hint.hidden = false;
        }

        /* ----------------------------------------------------------------------
           Park the chosen ingredient at twelve o'clock and hold it there.

           The arm places its node at rotate(a) translateX(r), where 0deg points
           right, so twelve o'clock is -90deg and the ring has to sit at
           (-90 - a) for THIS node to land on top.

           Handing over from the CSS animation to a transition takes three steps
           and the order is load-bearing:

             1. read the live angle out of the computed matrix and re-state it as
                an inline transform with animation:none. Without this the ring
                would snap back to 0deg the instant the animation is cancelled.
             2. force a reflow, so that frozen angle is the committed value the
                transition will start from.
             3. only then add .is-parked (which turns the transition on) and write
                the destination angle.

           Every counter is driven to exactly the negated angle rather than being
           read individually. They share one timeline with the ring, so their
           angle IS the negation, and deriving it keeps the nodes upright to the
           bit instead of to whatever four decimal places each matrix rounded to.
        ---------------------------------------------------------------------- */
        park(key) {
          if (!this.ring) return;
          var node = this.nodes.filter(function (n) {
            return n.dataset.orbKey === key;
          })[0];
          if (!node) return;

          var arm = parseFloat(node.dataset.orbAngle);
          if (isNaN(arm)) return;

          var from = this.parked == null ? this.angleOf(this.ring) : this.parked;
          var target = -90 - arm;
          // shortest way round, so a node never takes the long way to the top
          var delta = ((((target - from + 180) % 360) + 360) % 360) - 180;
          var to = from + delta;

          this.freeze(from);
          void this.ring.offsetWidth;
          this.stage.classList.add('is-parked');
          this.write(to);
          this.parked = to;
        }

        /* Hand back to the animation without a jump: a negative animation-delay
           starts the timeline at the phase that already matches the parked
           angle. The counters take the SAME delay, because reverse playback maps
           elapsed fraction f to rotate(-360f), which is the negation we want. */
        unpark() {
          if (!this.ring || this.parked == null) return;

          var dur = parseFloat(getComputedStyle(this.stage).getPropertyValue('--orb-dur'));
          if (isNaN(dur) || dur <= 0) dur = 44;
          var phase = ((this.parked % 360) + 360) % 360;
          var delay = -(phase / 360) * dur + 's';

          this.stage.classList.remove('is-parked');
          this.each(function (el) {
            el.style.transform = '';
            el.style.animation = '';
            el.style.animationDelay = delay;
          });
          this.parked = null;
        }

        freeze(deg) {
          this.each(function (el, isCounter) {
            el.style.animation = 'none';
            el.style.transform = 'rotate(' + (isCounter ? -deg : deg) + 'deg)';
          });
        }

        write(deg) {
          this.each(function (el, isCounter) {
            el.style.transform = 'rotate(' + (isCounter ? -deg : deg) + 'deg)';
          });
        }

        each(fn) {
          fn(this.ring, false);
          this.counters.forEach(function (c) {
            fn(c, true);
          });
        }

        /* Pull the rotation back out of the computed matrix. The ring only ever
           rotates, so atan2 of the first column is the whole story. */
        angleOf(el) {
          var t = getComputedStyle(el).transform;
          var m = t && t.match(/matrix\(([^)]+)\)/);
          if (!m) return 0;
          var p = m[1].split(',');
          return (Math.atan2(parseFloat(p[1]), parseFloat(p[0])) * 180) / Math.PI;
        }
      }
    );
  }

  /* --------------------------------------------------------------------------
     <unc-faq-tabs>
     Category pills that filter the accordion rows below them.

     Every row is server-rendered and the JSON-LD covers all of them regardless
     of which pill is active, so filtering never hides anything from search.
     Without JS the pills are inert and every row is visible, which is a
     perfectly usable FAQ.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-faq-tabs')) {
    customElements.define(
      'unc-faq-tabs',
      class UncFaqTabs extends HTMLElement {
        connectedCallback() {
          this.pills = Array.from(this.querySelectorAll('[data-faq-pill]'));
          this.items = Array.from(this.querySelectorAll('[data-faq-cat]'));
          if (!this.pills.length || !this.items.length) return;

          this.onClick = this.onClick.bind(this);
          this.addEventListener('click', this.onClick);

          var active = this.pills.filter(function (p) {
            return p.getAttribute('aria-selected') === 'true';
          })[0];
          this.select((active || this.pills[0]).dataset.faqPill);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
        }

        onClick(e) {
          var pill = e.target.closest('[data-faq-pill]');
          if (!pill) return;
          this.select(pill.dataset.faqPill);
        }

        select(cat) {
          this.pills.forEach(function (p) {
            p.setAttribute('aria-selected', p.dataset.faqPill === cat ? 'true' : 'false');
          });
          this.items.forEach(function (it) {
            var show = cat === '*' || it.dataset.faqCat === cat;
            it.hidden = !show;
            // Collapse anything being hidden, so reopening a category does not
            // reveal a row that was left open underneath.
            if (!show) {
              var d = it.matches('details') ? it : it.querySelector('details');
              if (d) d.open = false;
            }
          });
        }
      }
    );
  }

  /* --------------------------------------------------------------------------
     <unc-hscroll>
     Progress dots for a horizontally scrolling card row on mobile. Purely
     additive: the row scrolls with or without this, the dots just show where
     you are.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-hscroll')) {
    customElements.define(
      'unc-hscroll',
      class UncHscroll extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-hscroll-track]');
          this.dots = Array.from(this.querySelectorAll('[data-hscroll-dot]'));
          if (!this.track || !this.dots.length) return;

          this.onScroll = this.onScroll.bind(this);
          this.onDotClick = this.onDotClick.bind(this);
          this.track.addEventListener('scroll', this.onScroll, { passive: true });
          this.addEventListener('click', this.onDotClick);
          this.onScroll();

          /* Slow autoplay. Opt-in per instance via data-autoplay="<seconds>", so
             the product page's card rows are unaffected unless asked. */
          var secs = parseFloat(this.dataset.autoplay || '0');
          if (secs > 0) {
            var self = this;
            this.stopAuto = uncAutoCycle(
              this,
              function () {
                var i = self.dots.findIndex(function (d) {
                  return d.classList.contains('is-on');
                });
                self.goTo((i + 1) % self.dots.length);
              },
              secs * 1000
            );
          }
        }

        disconnectedCallback() {
          if (this.track) this.track.removeEventListener('scroll', this.onScroll);
          this.removeEventListener('click', this.onDotClick);
          if (this.stopAuto) this.stopAuto();
        }

        /* The comp's dots are clickable, not just an indicator. Scrolls the Nth
           card to centre, which is where scroll-snap will hold it. */
        onDotClick(e) {
          var dot = e.target.closest('[data-hscroll-dot]');
          if (!dot || !this.contains(dot)) return;
          this.goTo(this.dots.indexOf(dot));
        }

        // Shared by the dots and by autoplay, so both centre a card identically.
        goTo(i) {
          var card = this.track.children[i];
          if (!card) return;
          this.track.scrollTo({
            left: card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2,
            behavior: 'smooth',
          });
        }

        onScroll() {
          var max = this.track.scrollWidth - this.track.clientWidth;
          var ratio = max > 0 ? this.track.scrollLeft / max : 0;
          var i = Math.round(ratio * (this.dots.length - 1));
          this.dots.forEach(function (d, n) {
            d.classList.toggle('is-on', n === i);
          });
        }
      }
    );
  }

  /* --------------------------------------------------------------------------
     <unc-quotes>
     Testimonial spotlight. One quote visible, an avatar chip per person.

     A proper ARIA tablist: the chips are the tabs, the quotes are the panels,
     and only the selected chip is in the tab order. Arrow keys move between
     chips, Home and End jump to the ends, which is what a tablist is expected
     to do and what a plain row of buttons would not give.

     Selection state lives in the DOM and nowhere else: aria-selected on the
     chip drives every visual, including the greyscale avatar and the collapsing
     label column, through CSS attribute selectors. There is no parallel state
     object to fall out of sync.

     Degrades cleanly: with this file absent the first quote is the one the
     server marked .is-active, so a visitor still reads a full testimonial. The
     chips are inert but still show every name.
  -------------------------------------------------------------------------- */
  /* --------------------------------------------------------------------------
     Animated <details> rows for the FAQ.

     A bare <details> snaps open and shut, because the browser stops rendering
     the non-summary children the instant `open` goes away. The comp animates
     grid-template-rows from 0fr to 1fr in both directions, so on CLOSE the
     `open` attribute has to be held on until the collapse has finished.

     Opening:  set open, paint at 0fr, then release to 1fr on the next frame.
     Closing:  cancel the default, run 1fr to 0fr, drop `open` on transitionend.

     With this file absent the rows still open and close natively, just without
     the tween. That is why it stays a <details> rather than a div and a button.
  -------------------------------------------------------------------------- */
  function uncAnimateDetails(root) {
    var items = root.querySelectorAll('details.unc-hp-faq__item');
    if (!items.length) return;

    Array.prototype.forEach.call(items, function (d) {
      if (d.__uncAnimated) return;
      d.__uncAnimated = true;

      var panel = d.querySelector('.unc-pdp-faq__a');
      var summary = d.querySelector('summary');
      if (!panel || !summary) return;

      if (d.open) d.classList.add('is-open');

      summary.addEventListener('click', function (e) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        e.preventDefault();
        if (d.__uncBusy) return;
        d.__uncBusy = true;

        var done = function () {
          panel.removeEventListener('transitionend', done);
          d.__uncBusy = false;
          d.classList.remove('is-opening', 'is-closing');
          if (d.__uncClosing) {
            d.open = false;
            d.classList.remove('is-open');
            d.__uncClosing = false;
          } else {
            d.classList.add('is-open');
          }
        };

        panel.addEventListener('transitionend', done);
        /* transitionend does not fire if the row is already at its target size,
           so a timeout guarantees the busy flag is released. */
        setTimeout(function () {
          if (d.__uncBusy) done();
        }, 400);

        if (d.open) {
          d.__uncClosing = true;
          d.classList.remove('is-open');
          /* Force a frame at the open height before collapsing, or the browser
             coalesces both states and there is nothing to interpolate. */
          void d.offsetHeight;
          d.classList.add('is-closing');
        } else {
          d.__uncClosing = false;
          d.classList.add('is-opening');
          d.open = true;
          void d.offsetHeight;
          d.classList.remove('is-opening');
          d.classList.add('is-open');
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      uncAnimateDetails(document);
    });
  } else {
    uncAnimateDetails(document);
  }

  /* The theme editor re-renders a section without a full page load. */
  document.addEventListener('shopify:section:load', function (e) {
    uncAnimateDetails(e.target);
  });

  /* --------------------------------------------------------------------------
     <unc-gallery>
     Buy box images: one main shot, a clickable thumbnail strip beneath it.

     A proper ARIA tablist, same shape as <unc-quotes>: thumbs are the tabs, the
     full-size images are the panels, only the selected thumb is in the tab
     order, and arrow keys move between them.

     Selection lives in the DOM and nowhere else. aria-selected on the thumb
     drives its own highlight through a CSS attribute selector, and .is-active on
     the panel drives the cross-fade. No parallel state object to desync.

     Degrades cleanly: with this file missing the first image is the one the
     server marked .is-active, so a visitor still sees the pack shot. The thumbs
     are inert but still show what else there is.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-gallery')) {
    customElements.define(
      'unc-gallery',
      class UncGallery extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.from(this.querySelectorAll('[data-gallery-tab]'));
          this.panels = Array.from(this.querySelectorAll('[data-gallery-panel]'));
          if (this.tabs.length < 2 || !this.panels.length) return;

          this.onClick = this.onClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.addEventListener('click', this.onClick);
          this.addEventListener('keydown', this.onKeydown);

          /* THE ARROWS AND THE EDGE FADES.

             A scroller that simply clips at its edge looks like the end of the
             gallery, so the later pictures never get found. These say otherwise,
             and they say it honestly: the state is recomputed from the real
             scroll position, so the arrows are only offered when there is
             genuinely somewhere to go, and both disappear entirely when the row
             fits without scrolling. */
          var startAt = this.tabs.findIndex(function (t) {
            return t.getAttribute('aria-selected') === 'true';
          });
          this.current = startAt === -1 ? 0 : startAt;

          /* THE 3D PACKS MOVE, THEY ARE NOT RENDERED TWICE.

             Desktop wants them as their own labelled row; a phone cannot spare a
             whole band for two tiles, so there they ride at the end of the photo
             scroller. Rendering both would put two tabs on every panel and send
             every thumb to the wrong picture, so the same nodes are moved
             instead. They stay last in document order in either home, so the
             tab list built above is still in step with the panels. */
          this.modelsRow = this.querySelector('[data-models]');
          if (this.modelsRow) {
            var host = this;
            this.mq = window.matchMedia('(min-width: 990px)');
            this.placeModels = function () {
              var packs = Array.prototype.slice.call(
                host.querySelectorAll('.unc-pdp-hero__thumb--model')
              );
              var target = host.mq.matches ? host.modelsRow : host.querySelector('[data-thumbs]');
              if (!target) return;
              packs.forEach(function (p) {
                if (p.parentNode !== target) target.appendChild(p);
              });
              host.modelsRow.hidden = !host.mq.matches;
              if (host.stripState) host.stripState();
            };
            if (this.mq.addEventListener) this.mq.addEventListener('change', this.placeModels);
            else if (this.mq.addListener) this.mq.addListener(this.placeModels);
            this.placeModels();
          }

          /* SWIPE THE PICTURE, WHICH IS WHAT A PHONE EXPECTS. The thumb strip
             already scrolled with a finger because it is a real overflow
             scroller; the big image did not move at all, so the obvious gesture
             did nothing and the arrows were the only way through.

             Four things it deliberately does not do:

             - It ignores the mouse. A pointer has the arrows, the thumbs and the
               keyboard, and a click-drag over a photograph is how people select
               and save it.
             - It ignores the 3D panels. Drag to rotate is the whole point of
               the viewer and belongs to Pacdora, not to us.
             - It waits until the gesture is more sideways than not before
               claiming it, so a finger travelling down the page still scrolls.
               CSS `touch-action: pan-y` on the photo panels is the other half
               of that bargain: vertical stays the browser's, horizontal is ours.
             - It never calls preventDefault, so nothing is broken if the
               heuristic gets it wrong. */
          this.stage = this.querySelector('[data-stage]');
          if (this.stage && window.PointerEvent) {
            var g = this;
            var sx = 0;
            var sy = 0;
            var pid = null;
            var sideways = false;

            this.onSwipeDown = function (e) {
              if (e.pointerType === 'mouse') return;
              if (e.target.closest('.unc-pdp-hero__panel--model, button, a')) return;
              pid = e.pointerId;
              sx = e.clientX;
              sy = e.clientY;
              sideways = false;
            };
            this.onSwipeMove = function (e) {
              if (e.pointerId !== pid) return;
              // The 6px lets a straight-down flick stay vertical rather than
              // being claimed by a pixel or two of horizontal wobble.
              if (Math.abs(e.clientX - sx) > Math.abs(e.clientY - sy) + 6) sideways = true;
            };
            this.onSwipeUp = function (e) {
              if (e.pointerId !== pid) return;
              var dx = e.clientX - sx;
              pid = null;
              if (!sideways || Math.abs(dx) < 40) return;
              // Drag left, move forward: the picture follows the finger.
              g.stepPhoto(dx < 0 ? 1 : -1);
            };
            this.onSwipeCancel = function () {
              pid = null;
            };

            this.stage.addEventListener('pointerdown', this.onSwipeDown, { passive: true });
            this.stage.addEventListener('pointermove', this.onSwipeMove, { passive: true });
            this.stage.addEventListener('pointerup', this.onSwipeUp, { passive: true });
            this.stage.addEventListener('pointercancel', this.onSwipeCancel, { passive: true });
          }

          this.strip = this.querySelector('[data-strip]');
          this.scroller = this.querySelector('[data-thumbs]');
          if (this.strip && this.scroller) {
            var self = this;
            this.stripState = function () {
              var el = self.scroller;
              var max = el.scrollWidth - el.clientWidth;
              self.strip.toggleAttribute('data-overflow', max > 4);
              self.strip.toggleAttribute('data-at-start', el.scrollLeft <= 2);
              self.strip.toggleAttribute('data-at-end', el.scrollLeft >= max - 2);
            };
            this.onStripScroll = this.stripState;
            this.scroller.addEventListener('scroll', this.onStripScroll, { passive: true });
            if (window.ResizeObserver) {
              this.stripRO = new ResizeObserver(this.stripState);
              this.stripRO.observe(this.scroller);
            }
            this.stripState();
          }

          /* AUTOPLAY IS A REQUEST, NOT A GUARANTEE. The markup carries the
             attribute and the files are muted and inline, which is all any
             engine asks for, and they still came back paused with the data
             fully loaded. play() returns a promise that rejects when a policy
             declines, so it is caught and the poster frame simply stays. */
          if (this.dataset.motionReduced === 'run') {
            /* SPEED (31 Oct): the pack thumbnails marked data-lazyplay are started by the script in snippets/unc-gallery-media.liquid when they are on screen */
            Array.prototype.forEach.call(this.querySelectorAll('video:not([data-lazyplay])'), function (v) {
              var go = v.play();
              if (go && typeof go.catch === 'function') go.catch(function () {});
            });
          }

          /* A LOOPING THUMB IS MOTION LIKE ANY OTHER. The markup carries
             `autoplay` so the loop works for everyone by default and without
             JS; this pauses it back to its poster frame when the visitor has
             asked for less movement. Pausing, not hiding: the still is the
             point, and the badge on top of it still says 3D. */
          if (
            window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
            this.dataset.motionReduced !== 'run'
          ) {
            Array.prototype.forEach.call(this.querySelectorAll('video'), function (v) {
              v.removeAttribute('autoplay');
              v.autoplay = false;
              try {
                v.pause();
              } catch (e) {}
            });
          }
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.removeEventListener('keydown', this.onKeydown);
          if (this.scroller && this.onStripScroll) {
            this.scroller.removeEventListener('scroll', this.onStripScroll);
          }
          if (this.stripRO) this.stripRO.disconnect();
          if (this.stage && this.onSwipeDown) {
            this.stage.removeEventListener('pointerdown', this.onSwipeDown);
            this.stage.removeEventListener('pointermove', this.onSwipeMove);
            this.stage.removeEventListener('pointerup', this.onSwipeUp);
            this.stage.removeEventListener('pointercancel', this.onSwipeCancel);
          }
          if (this.mq && this.placeModels) {
            if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.placeModels);
            else if (this.mq.removeListener) this.mq.removeListener(this.placeModels);
          }
        }

        onClick(e) {
          // Checked before the tab, because it lives inside a panel rather than
          // in the thumb strip and must never be counted as one.
          var home = e.target.closest('[data-gallery-home]');
          if (home && this.contains(home)) {
            this.select(0, true);
            return;
          }
          /* CYCLE THROUGH THE PHOTOGRAPHS ONLY. The tab list also holds the 3D
             packs, so stepping blindly by one would drop the reader into a
             third-party viewer from an arrow labelled "next image". The photo
             tabs are marked in the markup and the walk stays inside them,
             wrapping at both ends. */
          var cyc = e.target.closest('[data-gallery-prev], [data-gallery-next]');
          if (cyc && this.contains(cyc)) {
            this.stepPhoto(cyc.hasAttribute('data-gallery-prev') ? -1 : 1);
            return;
          }

          var tab = e.target.closest('[data-gallery-tab]');
          if (!tab || !this.contains(tab)) return;
          this.select(this.tabs.indexOf(tab), false);
        }

        /* WALK THE PHOTOGRAPHS ONLY, wrapping at both ends. The tab list also
           holds the 3D packs, so stepping blindly by one would drop the reader
           into a third-party viewer from a control that said "next image". The
           photo tabs are marked in the markup and the walk stays inside them.

           Shared by the cycle arrows and the swipe, which are the same action
           asked for two ways. */
        stepPhoto(step) {
          var photos = this.tabs.filter(function (t) {
            return t.hasAttribute('data-gallery-photo');
          });
          if (!photos.length) return;
          var cur = photos.indexOf(this.tabs[this.current]);
          // Not on a photo (a 3D pack is open), so step in from the edge.
          var next =
            cur === -1
              ? photos[step > 0 ? 0 : photos.length - 1]
              : photos[(cur + step + photos.length) % photos.length];
          this.select(this.tabs.indexOf(next), false);
        }

        onKeydown(e) {
          var tab = e.target.closest('[data-gallery-tab]');
          if (!tab || !this.contains(tab)) return;
          var last = this.tabs.length - 1;
          var i = this.tabs.indexOf(tab);
          var next = -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = last;
          else return;
          e.preventDefault();
          this.select(next, true);
        }

        select(i, focus) {
          if (i < 0 || i >= this.tabs.length) return;
          // The cycle arrows need to know where they are starting from.
          this.current = i;
          this.tabs.forEach(function (t, n) {
            var on = n === i;
            t.setAttribute('aria-selected', on ? 'true' : 'false');
            t.setAttribute('tabindex', on ? '0' : '-1');
          });
          this.panels.forEach(function (p, n) {
            var on = n === i;
            p.classList.toggle('is-active', on);

            if (on) {
              /* THE THIRD PARTY IS NOT CONTACTED UNTIL ASKED FOR. The iframe
                 ships with no src at all, only data-src, so opening the page
                 costs no request, no cookie and no consent question. Copied
                 across once, on the first open, and left alone after that so
                 returning to the panel does not reload the viewer. */
              var frame = p.querySelector('iframe[data-src]');
              if (frame && !frame.getAttribute('src')) {
                frame.setAttribute('src', frame.getAttribute('data-src'));
              }
            } else {
              // A hidden loop is still decoding. Stop anything that just closed.
              Array.prototype.forEach.call(p.querySelectorAll('video'), function (v) {
                try {
                  v.pause();
                } catch (e) {}
              });
            }
          });
          if (focus) this.tabs[i].focus();

          /* THE STRIP FOLLOWS THE PICTURE, ALWAYS. This used to sit inside the
             `if (focus)` above, so it only ran for keyboard moves. Now that the
             arrows beside the thumbs are the main way through the gallery, that
             would have left the selected thumb scrolled off the end while the
             photograph advanced: pressing next would appear to do nothing to
             the row it is standing in. */
          /* CENTRED BY HAND, NOT BY scrollIntoView.

             `inline: 'nearest'` scrolls the least it can get away with, which
             lands the thumb hard against the edge, and scroll-snap then re-snaps
             the row and takes back the last few pixels: the strip visibly moved
             while the chosen thumb stayed half cut off. Measured it stepping 80px
             per press with the selection clipped the whole way from photo 5 to 9.

             Centring is also just better here. The next thumbs either side are
             what tell you the row keeps going. */
          if (this.scroller && this.scroller.contains(this.tabs[i])) {
            var er = this.tabs[i].getBoundingClientRect();
            var sr = this.scroller.getBoundingClientRect();
            this.scroller.scrollLeft += er.left + er.width / 2 - (sr.left + sr.width / 2);
            if (this.stripState) {
              this.stripState();
              setTimeout(this.stripState, 450);
            }
          } else if (this.tabs[i].scrollIntoView) {
            this.tabs[i].scrollIntoView({ block: 'nearest', inline: 'nearest' });
            // The strip has moved, so the fades have to be told even if no
            // scroll event arrives: some engines skip it for programmatic
            // scrolling. Once now, once after the smooth scroll has landed.
            if (this.stripState) {
              this.stripState();
              setTimeout(this.stripState, 450);
            }
          }
        }
      }
    );
  }

  if (!customElements.get('unc-quotes')) {
    customElements.define(
      'unc-quotes',
      class UncQuotes extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.from(this.querySelectorAll('[data-quote-tab]'));
          this.panels = Array.from(this.querySelectorAll('[data-quote-panel]'));
          if (this.tabs.length < 2 || !this.panels.length) return;

          this.onClick = this.onClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.addEventListener('click', this.onClick);
          this.addEventListener('keydown', this.onKeydown);

          /* Slow autoplay, opt-in via data-autoplay="<seconds>". Advancing with
             focus:false is deliberate: moving focus on a timer would yank the
             page around under a keyboard user. */
          var secs = parseFloat(this.dataset.autoplay || '0');
          if (secs > 0) {
            var self = this;
            this.stopAuto = uncAutoCycle(
              this,
              function () {
                var i = self.tabs.findIndex(function (t) {
                  return t.getAttribute('aria-selected') === 'true';
                });
                self.select((i + 1) % self.tabs.length, false);
              },
              secs * 1000
            );
          }
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.removeEventListener('keydown', this.onKeydown);
          if (this.stopAuto) this.stopAuto();
        }

        onClick(e) {
          var tab = e.target.closest('[data-quote-tab]');
          if (!tab || !this.contains(tab)) return;
          this.select(this.tabs.indexOf(tab), false);
        }

        onKeydown(e) {
          var tab = e.target.closest('[data-quote-tab]');
          if (!tab || !this.contains(tab)) return;

          var last = this.tabs.length - 1;
          var i = this.tabs.indexOf(tab);
          var next = -1;

          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = last;
          else return;

          e.preventDefault();
          this.select(next, true);
        }

        select(i, focus) {
          if (i < 0 || i >= this.tabs.length) return;

          this.tabs.forEach(function (t, n) {
            var on = n === i;
            t.setAttribute('aria-selected', on ? 'true' : 'false');
            t.setAttribute('tabindex', on ? '0' : '-1');
          });

          this.panels.forEach(function (p, n) {
            p.classList.toggle('is-active', n === i);
          });

          if (focus) this.tabs[i].focus();
        }
      }
    );
  }
  /* Shared with assets/unc-fs.js, which drives the Founder's Story dependency
     loop. Exported rather than copied so there is ONE set of pause rules for
     every auto-advancing thing on the site: hover, focus-within, off-screen,
     hidden tab, and reduced motion. A second implementation would drift. */
  window.uncAutoCycle = uncAutoCycle;
})();

/* ----------------------------------------------------------------------------
   HOMEPAGE HERO SLIDE-OVER DIMMING (Tim, 10 Oct). Same as unc-sc.js heroDim:
   writes --unc-hp-dim 0..1 from how far the stage has scrolled past the top of
   the viewport; unc-hp.css multiplies it into the dim layer's opacity. Only runs
   when the hero's "Slide-over distance" setting is above 0 (.unc-hp-herostage).
   A colour change tied to scroll, nothing moves, so not gated on reduced motion.
---------------------------------------------------------------------------- */
(function () {
  var stage = document.querySelector('.unc-hp-herostage');
  if (!stage) return;
  var hero = stage.querySelector('.unc-hp-hero');
  if (!hero) return;
  var queued = false;

  function paint() {
    queued = false;
    // The homepage hero can be taller than the window (Science's never is).
    // Pinning it at top: 0 would hold it with its bottom edge, where the band
    // arrives, off-screen. So it pins when its BOTTOM meets the window's
    // bottom: a negative sticky top of window height minus hero height.
    var pin = Math.min(0, window.innerHeight - hero.offsetHeight);
    hero.style.top = pin + 'px';
    var glide = stage.offsetHeight - hero.offsetHeight;
    if (glide <= 0) {
      hero.style.setProperty('--unc-hp-dim', '0');
      return;
    }
    var gone = pin - stage.getBoundingClientRect().top;
    var p = Math.min(Math.max(gone / glide, 0), 1);
    hero.style.setProperty('--unc-hp-dim', p.toFixed(3));
  }

  function onScroll() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(paint);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  paint();
})();

/* ----------------------------------------------------------------------------
   HOMEPAGE CARD LIFT (Tim, 10 Oct). The floating buy-box card hangs over the
   section after it; as that section's top comes up from the bottom of the
   screen to about 55% height, --unc-hp-lift goes 0 -> 1 and unc-hp.css moves
   the card up by its hang, so it slides off instead of staying on top. Reads
   the NEXT section's position, which the card's transform doesn't move, so
   there's no feedback loop.
---------------------------------------------------------------------------- */
(function () {
  if (!document.querySelector('.unc-hp-herostage')) return;
  var plans = document.querySelector('.unc-sec--plans');
  var wrap = plans && plans.closest('.shopify-section');
  var next = wrap && wrap.nextElementSibling;
  /* A bottom ticker strip is part of the card, so lift against the section after it. */
  /* The home page can hold a second layout that is display:none at this width (31 Oct); never lift against a hidden section. */
  function skipHidden() { while (next && next.offsetParent === null && next.nextElementSibling) next = next.nextElementSibling; }
  skipHidden();
  if (next && next.querySelector('.unc-sec--ticker-end')) next = next.nextElementSibling;
  skipHidden();
  var main = wrap && wrap.parentElement;
  if (!next || !main) return;
  var queued = false;

  function paint() {
    queued = false;
    var vh = window.innerHeight;
    var top = next.getBoundingClientRect().top;
    var p = (vh - top) / (vh * 0.45);
    p = Math.min(Math.max(p, 0), 1);
    main.style.setProperty('--unc-hp-lift', p.toFixed(3));
  }

  function onScroll() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(paint);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  paint();
})();
